import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { hash } from '@ember/helper';
import { next } from '@ember/runloop';
import { ariaListbox } from 'ember-aria-voyager';
import { TrackedArray } from 'tracked-built-ins';
import styles from '@hokulea/core/controls.module.css';
import type Owner from '@ember/owner';
import type { WithBoundArgs } from '@glint/template';
export interface OptionSignature<V> {
    Element: HTMLOptionElement;
    Args: {
        value: V;
        isSelected: (option: V) => boolean;
        registerItem: (item: V) => void;
        unregisterItem: (item: V) => void;
    };
    Blocks: {
        default: [];
    };
}
let Option = class Option<V> extends Component<OptionSignature<V>> {
    constructor(owner1: Owner, args1: OptionSignature<V>['Args']){
        super(owner1, args1);
        args1.registerItem(args1.value);
        registerDestructor(this, ()=>{
            args1.unregisterItem(args1.value);
        });
    }
    static{
        template(`
    <span role="option" aria-selected={{if (@isSelected @value) "true"}}>
      {{yield}}
    </span>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
export interface ListSignature<V> {
    Element: HTMLDivElement;
    Args: {
        multiple?: boolean;
        disabled?: boolean;
        value?: V | V[];
        update?: (value: V | V[]) => void;
        activateItem?: (value: V) => void;
    };
    Blocks: {
        default: [{
                Option: WithBoundArgs<typeof Option, 'isSelected' | 'registerItem' | 'unregisterItem'>;
            }];
    };
}
export default class List<V> extends Component<ListSignature<V>> {
    Option = Option<V>;
    @tracked
    items: V[] = new TrackedArray();
    registerItem = (item1: V)=>{
        // eslint-disable-next-line ember/no-runloop
        next(()=>{
            this.items.push(item1);
        });
    };
    unregisterItem = (item1: V)=>{
        // eslint-disable-next-line ember/no-runloop
        next(()=>{
            this.items.splice(this.items.indexOf(item1), 1);
        });
    };
    isSelected = (option1: V)=>{
        if (Array.isArray(this.args.value)) {
            return this.args.value.includes(option1);
        }
        return this.args.value === option1;
    };
    static{
        template(`
    <div
      class={{styles.list}}
      data-test-list
      ...attributes
      {{!@glint-ignore}}
      {{ariaListbox
        items=this.items
        selection=@value
        multi=@multiple
        disabled=@disabled
        select=@update
        activateItem=@activateItem
      }}
    >
      {{yield
        (hash
          Option=(component
            this.Option
            isSelected=this.isSelected
            registerItem=this.registerItem
            unregisterItem=this.unregisterItem
          )
        )
      }}
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi9ob21lL3J1bm5lci93b3JrL2hva3VsZWEvaG9rdWxlYS9lbWJlci9wYWNrYWdlL3NyYy9jb21wb25lbnRzL2xpc3QuZ3RzIl0sInNvdXJjZXNDb250ZW50IjpbImltcG9ydCBDb21wb25lbnQgZnJvbSAnQGdsaW1tZXIvY29tcG9uZW50JztcbmltcG9ydCB7IHRyYWNrZWQgfSBmcm9tICdAZ2xpbW1lci90cmFja2luZyc7XG5pbXBvcnQgeyByZWdpc3RlckRlc3RydWN0b3IgfSBmcm9tICdAZW1iZXIvZGVzdHJveWFibGUnO1xuaW1wb3J0IHsgaGFzaCB9IGZyb20gJ0BlbWJlci9oZWxwZXInO1xuaW1wb3J0IHsgbmV4dCB9IGZyb20gJ0BlbWJlci9ydW5sb29wJztcblxuaW1wb3J0IHsgYXJpYUxpc3Rib3ggfSBmcm9tICdlbWJlci1hcmlhLXZveWFnZXInO1xuaW1wb3J0IHsgVHJhY2tlZEFycmF5IH0gZnJvbSAndHJhY2tlZC1idWlsdC1pbnMnO1xuXG5pbXBvcnQgc3R5bGVzIGZyb20gJ0Bob2t1bGVhL2NvcmUvY29udHJvbHMubW9kdWxlLmNzcyc7XG5cbmltcG9ydCB0eXBlIE93bmVyIGZyb20gJ0BlbWJlci9vd25lcic7XG5pbXBvcnQgdHlwZSB7IFdpdGhCb3VuZEFyZ3MgfSBmcm9tICdAZ2xpbnQvdGVtcGxhdGUnO1xuXG5leHBvcnQgaW50ZXJmYWNlIE9wdGlvblNpZ25hdHVyZTxWPiB7XG4gIEVsZW1lbnQ6IEhUTUxPcHRpb25FbGVtZW50O1xuICBBcmdzOiB7XG4gICAgdmFsdWU6IFY7XG4gICAgaXNTZWxlY3RlZDogKG9wdGlvbjogVikgPT4gYm9vbGVhbjtcbiAgICByZWdpc3Rlckl0ZW06IChpdGVtOiBWKSA9PiB2b2lkO1xuICAgIHVucmVnaXN0ZXJJdGVtOiAoaXRlbTogVikgPT4gdm9pZDtcbiAgfTtcbiAgQmxvY2tzOiB7XG4gICAgZGVmYXVsdDogW107XG4gIH07XG59XG5cbmNsYXNzIE9wdGlvbjxWPiBleHRlbmRzIENvbXBvbmVudDxPcHRpb25TaWduYXR1cmU8Vj4-IHtcbiAgY29uc3RydWN0b3Iob3duZXI6IE93bmVyLCBhcmdzOiBPcHRpb25TaWduYXR1cmU8Vj5bJ0FyZ3MnXSkge1xuICAgIHN1cGVyKG93bmVyLCBhcmdzKTtcblxuICAgIGFyZ3MucmVnaXN0ZXJJdGVtKGFyZ3MudmFsdWUpO1xuXG4gICAgcmVnaXN0ZXJEZXN0cnVjdG9yKHRoaXMsICgpID0-IHtcbiAgICAgIGFyZ3MudW5yZWdpc3Rlckl0ZW0oYXJncy52YWx1ZSk7XG4gICAgfSk7XG4gIH1cblxuICA8dGVtcGxhdGU-XG4gICAgPHNwYW4gcm9sZT1cIm9wdGlvblwiIGFyaWEtc2VsZWN0ZWQ9e3tpZiAoQGlzU2VsZWN0ZWQgQHZhbHVlKSBcInRydWVcIn19PlxuICAgICAge3t5aWVsZH19XG4gICAgPC9zcGFuPlxuICA8L3RlbXBsYXRlPlxufVxuXG5leHBvcnQgaW50ZXJmYWNlIExpc3RTaWduYXR1cmU8Vj4ge1xuICBFbGVtZW50OiBIVE1MRGl2RWxlbWVudDtcbiAgQXJnczoge1xuICAgIG11bHRpcGxlPzogYm9vbGVhbjtcbiAgICBkaXNhYmxlZD86IGJvb2xlYW47XG4gICAgdmFsdWU_OiBWIHwgVltdO1xuICAgIHVwZGF0ZT86ICh2YWx1ZTogViB8IFZbXSkgPT4gdm9pZDtcbiAgICBhY3RpdmF0ZUl0ZW0_OiAodmFsdWU6IFYpID0-IHZvaWQ7XG4gIH07XG4gIEJsb2Nrczoge1xuICAgIGRlZmF1bHQ6IFtcbiAgICAgIHtcbiAgICAgICAgT3B0aW9uOiBXaXRoQm91bmRBcmdzPHR5cGVvZiBPcHRpb248Vj4sICdpc1NlbGVjdGVkJyB8ICdyZWdpc3Rlckl0ZW0nIHwgJ3VucmVnaXN0ZXJJdGVtJz47XG4gICAgICB9XG4gICAgXTtcbiAgfTtcbn1cblxuZXhwb3J0IGRlZmF1bHQgY2xhc3MgTGlzdDxWPiBleHRlbmRzIENvbXBvbmVudDxMaXN0U2lnbmF0dXJlPFY-PiB7XG4gIE9wdGlvbiA9IE9wdGlvbjxWPjtcblxuICBAdHJhY2tlZCBpdGVtczogVltdID0gbmV3IFRyYWNrZWRBcnJheSgpO1xuXG4gIHJlZ2lzdGVySXRlbSA9IChpdGVtOiBWKSA9PiB7XG4gICAgLy8gZXNsaW50LWRpc2FibGUtbmV4dC1saW5lIGVtYmVyL25vLXJ1bmxvb3BcbiAgICBuZXh0KCgpID0-IHtcbiAgICAgIHRoaXMuaXRlbXMucHVzaChpdGVtKTtcbiAgICB9KTtcbiAgfTtcblxuICB1bnJlZ2lzdGVySXRlbSA9IChpdGVtOiBWKSA9PiB7XG4gICAgLy8gZXNsaW50LWRpc2FibGUtbmV4dC1saW5lIGVtYmVyL25vLXJ1bmxvb3BcbiAgICBuZXh0KCgpID0-IHtcbiAgICAgIHRoaXMuaXRlbXMuc3BsaWNlKHRoaXMuaXRlbXMuaW5kZXhPZihpdGVtKSwgMSk7XG4gICAgfSk7XG4gIH07XG5cbiAgaXNTZWxlY3RlZCA9IChvcHRpb246IFYpID0-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